/**
 * Your sign-in security: authenticator status and backup codes.
 *
 * The one place a signed-in person creates backup codes. On 2026-09-16 every
 * code issued before then was invalidated (they were stored in plaintext), so
 * this page is how existing accounts get a working set. Creating codes needs
 * a current code from the authenticator; the new set replaces the old one and
 * is shown once.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ShieldCheck } from 'lucide-react';
import { apiRequest } from '../services/api';
import { TENANT } from '../config';
import BackupCodesPanel from '../components/Auth/BackupCodesPanel';

type Status = { totpEnabled: boolean; backupCodesRemaining: number; enrolledAt?: string | null };

export default function AccountSecurity() {
  const [status, setStatus] = useState<Status | null>(null);
  const [loadError, setLoadError] = useState('');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [codes, setCodes] = useState<string[]>([]);

  const support = TENANT.branding.supportEmail;

  const load = useCallback(async () => {
    setLoadError('');
    const r = await apiRequest<Status>('GET', '/auth/2fa/status');
    if (r.success && r.data) { setStatus(r.data); return; }
    setLoadError(r.status === undefined ? 'Could not reach the server.' : (r.message || 'Your security settings could not be loaded.'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError('');
    const r = await apiRequest<{ backupCodes: string[] }>('POST', '/auth/2fa/backup-codes/regenerate', { otp });
    setBusy(false);
    if (r.success && r.data?.backupCodes) {
      setCodes(r.data.backupCodes);
      setOtp('');
      setStatus((s) => (s ? { ...s, backupCodesRemaining: r.data!.backupCodes.length } : s));
      return;
    }
    setError(r.status === 401
      ? 'That is not the current code from your authenticator app. Try the next one it shows.'
      : (r.message || 'Backup codes could not be created.'));
  };

  const left = status?.backupCodesRemaining ?? 0;

  return (
    <div className="min-h-screen bg-[#f6faf9] px-4 py-10 text-[#10252d]">
      <div className="mx-auto flex w-full max-w-lg flex-col gap-5">
        <div>
          <h1 className="text-xl font-semibold">Sign-in security</h1>
          <p className="mt-1 text-sm text-[#60747b]">Your authenticator app and the backup codes that replace it if your phone is lost.</p>
        </div>

        {loadError && (
          <div role="alert" className="rounded-md border border-[#e8c9c9] bg-[#fbeeee] px-4 py-3 text-sm text-[#b94343]">
            {loadError}{' '}
            <button type="button" onClick={load} className="underline">Try again</button>
          </div>
        )}

        {!status && !loadError && <Loader2 className="h-6 w-6 animate-spin text-[#60747b]" aria-label="Loading" />}

        {status && (
          <div className="rounded-lg border border-[#d7e5e1] bg-white p-5">
            <dl className="grid grid-cols-[auto,1fr] gap-x-6 gap-y-2 text-sm">
              <dt className="text-[#60747b]">Authenticator</dt>
              <dd>{status.totpEnabled ? 'Registered' : 'Not registered'}</dd>
              <dt className="text-[#60747b]">Backup codes left</dt>
              <dd className={left === 0 ? 'font-semibold text-[#b94343]' : left <= 3 ? 'font-semibold text-[#8a5610]' : ''}>
                {left}{left === 0 ? ' — create a set now' : left <= 3 ? ' — running low' : ''}
              </dd>
            </dl>
          </div>
        )}

        {status?.totpEnabled && codes.length === 0 && (
          <form onSubmit={create} className="flex flex-col gap-3 rounded-lg border border-[#d7e5e1] bg-white p-5">
            <h2 className="text-base font-semibold">Create new backup codes</h2>
            <p className="text-sm leading-6 text-[#60747b]">
              You get ten codes. Any codes you had before stop working.
            </p>
            {error && <p role="alert" className="text-sm text-[#b94343]">{error}</p>}
            <label className="text-sm font-medium">
              Current code from your authenticator app
              <input
                required value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric" autoComplete="one-time-code" placeholder="000000"
                className="mt-1 h-11 w-full rounded-md border border-[#d7e5e1] px-3 text-center font-mono text-lg tracking-[0.3em] outline-none focus:border-[#17685d]"
              />
            </label>
            <button
              type="submit" disabled={busy || otp.length !== 6}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#10252d] px-5 text-sm font-semibold text-white disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              Create backup codes
            </button>
          </form>
        )}

        {codes.length > 0 && <BackupCodesPanel codes={codes} note="Your earlier backup codes no longer work." />}

        <p className="text-xs leading-5 text-[#60747b]">
          Lost your phone and have no codes? Ask your administrator to reset your authenticator
          {support ? <> ({support})</> : null}. <Link to="/" className="underline">Back</Link>
        </p>
      </div>
    </div>
  );
}
